// components/DeleteConfirmDialog.jsx
'use client';
import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  TextField
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';

export default function DeleteConfirmDialog({
  open = false,
  title = 'Confirm Delete',
  message = 'Are you sure you want to delete this item?',
  loading = false,
  onClose = () => {},
  onConfirm = () => {},
  confirmLabel = 'Delete',
  cancelLabel = 'Cancel',
  itemName = '',
  requireInputMatch = false,
  matchText = ''
}) {
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    if (open) {
      setInputValue('');
    }
  }, [open]);
  return (
    <Dialog
      open={open}
      onClose={() => !loading && onClose()}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: 0,
          overflow: 'visible',
          boxShadow: '0 10px 30px rgba(8,14,30,0.12)'
        }
      }}
      aria-labelledby="delete-confirm-title"
      aria-describedby="delete-confirm-description"
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.12), rgba(255, 152, 0, 0.08))',
              border: '1px solid rgba(255,152,0,0.18)'
            }}
            aria-hidden="true"
          >
            <WarningAmberIcon sx={{ color: '#ff9800', fontSize: 28 }} />
          </Box>
          <DialogTitle id="delete-confirm-title" sx={{ p: 0, fontWeight: 700 }}>
            {title}
          </DialogTitle>
        </Box>

        <IconButton onClick={() => !loading && onClose()} size="small" sx={{ color: 'text.secondary' }} aria-label="close">
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent dividers sx={{ pt: 2 }}>
        <Typography id="delete-confirm-description" variant="body1" sx={{ mb: 1.25 }}>
          {message}
        </Typography>

        {itemName ? (
          <Box
            sx={{
              mt: 0.5,
              p: 1,
              borderRadius: 1,
              backgroundColor: 'action.hover',
              display: 'inline-block'
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {itemName}
            </Typography>
          </Box>
        ) : null}

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          This action cannot be undone. Please ensure you select the correct item.
        </Typography>

        {requireInputMatch && matchText && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              To confirm, type <strong>{matchText}</strong> below:
            </Typography>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={matchText}
              disabled={loading}
              autoComplete="off"
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={loading}
          sx={{
            textTransform: 'none',
            borderRadius: 1.5,
            px: 2,
            '&:hover': { boxShadow: 'none' }
          }}
        >
          {cancelLabel}
        </Button>

        <Button
          variant="contained"
          color="error"
          onClick={onConfirm}
          disabled={loading || (requireInputMatch && inputValue !== matchText)}
          startIcon={loading ? <CircularProgress color="inherit" size={18} thickness={5} /> : null}
          sx={{
            ml: 1,
            textTransform: 'none',
            borderRadius: 1.5,
            px: 2.5,
            fontWeight: 600,
            boxShadow: loading ? 'none' : '0 6px 18px rgba(211,47,47,0.16)',
            '&:hover': {
              transform: loading ? 'none' : 'translateY(-1px)'
            }
          }}
        >
          {loading ? 'Delete...' : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
